import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/addresses`;

/**
 * Address Service
 * Gerencia todas as operações CRUD de endereços
 */
class AddressService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all addresses
   * @returns {Promise<Array>}
   */
  async getAllAddresses() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar endereços:", error);
      throw error;
    }
  }

  /**
   * Get address by ID
   * @param {number} id - Address ID
   * @returns {Promise<object>}
   */
  async getAddressById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get addresses by user ID
   * @param {number} userId - User ID
   * @returns {Promise<Array>}
   */
  async getAddressesByUserId(userId) {
    try {
      const response = await axios.get(
        `${API_BASE}/user/${userId}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`❌ Erro ao buscar endereços do usuário ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Create new address
   * @param {object} addressData - Address data
   * @returns {Promise<object>}
   */
  async createAddress(addressData) {
    try {
      const response = await axios.post(
        API_BASE,
        addressData,
        this.getAuthHeaders()
      );
      console.log("✅ Endereço criado com sucesso");
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar endereço:", error);
      throw error;
    }
  }

  /**
   * Update address
   * @param {number} id - Address ID
   * @param {object} addressData - Address data
   * @returns {Promise<object>}
   */
  async updateAddress(id, addressData) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}`,
        addressData,
        this.getAuthHeaders()
      );
      console.log(`✅ Endereço ${id} atualizado com sucesso`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao atualizar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete address
   * @param {number} id - Address ID
   * @returns {Promise<void>}
   */
  async deleteAddress(id) {
    try {
      await axios.delete(`${API_BASE}/${id}`, this.getAuthHeaders());
      console.log(`✅ Endereço ${id} deletado com sucesso`);
    } catch (error) {
      console.error(`❌ Erro ao deletar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * Set address as default
   * @param {number} id - Address ID
   * @returns {Promise<object>}
   */
  async setAsDefault(id) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}/default`,
        {},
        this.getAuthHeaders()
      );
      console.log(`✅ Endereço ${id} definido como padrão`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao definir endereço ${id} como padrão:`, error);
      throw error;
    }
  }

  /**
   * Validate CEP format (Brazilian postal code)
   * @param {string} cep
   * @returns {boolean}
   */
  isValidCEP(cep) {
    // Remove non-numeric characters
    const cleanCEP = cep.replace(/\D/g, "");
    return cleanCEP.length === 8;
  }

  /**
   * Format CEP with mask (12345-678)
   * @param {string} cep
   * @returns {string}
   */
  formatCEP(cep) {
    const cleanCEP = cep.replace(/\D/g, "");
    if (cleanCEP.length !== 8) return cep;
    return cleanCEP.replace(/(\d{5})(\d{3})/, "$1-$2");
  }

  /**
   * Validate address data before submit
   * @param {object} addressData
   * @returns {object} {valid: boolean, errors: object}
   */
  validateAddressData(addressData) {
    const errors = {};

    // Street validation
    if (!addressData.street || !addressData.street.trim()) {
      errors.street = "Rua é obrigatória";
    }

    // Number validation
    if (!addressData.number || !addressData.number.trim()) {
      errors.number = "Número é obrigatório";
    }

    // Neighborhood validation
    if (!addressData.neighborhood || !addressData.neighborhood.trim()) {
      errors.neighborhood = "Bairro é obrigatório";
    }

    // City validation
    if (!addressData.city || !addressData.city.trim()) {
      errors.city = "Cidade é obrigatória";
    }

    // State validation
    if (!addressData.state || !addressData.state.trim()) {
      errors.state = "Estado é obrigatório";
    } else if (addressData.state.length !== 2) {
      errors.state = "Estado deve ter 2 caracteres (UF)";
    }

    // CEP validation
    if (!addressData.zipCode || !addressData.zipCode.trim()) {
      errors.zipCode = "CEP é obrigatório";
    } else if (!this.isValidCEP(addressData.zipCode)) {
      errors.zipCode = "CEP inválido (deve ter 8 dígitos)";
    }

    // User ID validation
    if (!addressData.userId) {
      errors.userId = "Usuário é obrigatório";
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Search address by CEP using ViaCEP API
   * @param {string} cep
   * @returns {Promise<object>}
   */
  async searchByCEP(cep) {
    try {
      const cleanCEP = cep.replace(/\D/g, "");
      if (cleanCEP.length !== 8) {
        throw new Error("CEP inválido");
      }

      const response = await axios.get(
        `https://viacep.com.br/ws/${cleanCEP}/json/`
      );

      if (response.data.erro) {
        throw new Error("CEP não encontrado");
      }

      return {
        street: response.data.logradouro || "",
        neighborhood: response.data.bairro || "",
        city: response.data.localidade || "",
        state: response.data.uf || "",
        zipCode: cleanCEP,
      };
    } catch (error) {
      console.error("❌ Erro ao buscar CEP:", error);
      throw error;
    }
  }
}

// Export singleton instance
const addressService = new AddressService();
export default addressService;
